import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, type ReactNode } from 'react'

import { hydrateIdentity, useAuth } from '@/lib/auth'

import { useAcceptPendingInvitations } from './pending-access-queries'
import type { PendingAccessFailure } from '../../api/invitations-api'

const FAILURE_REASONS: Readonly<Record<string, string>> = {
  INVITATION_GRANT_INVALID: 'the access it granted is no longer available',
  INVITATION_GRANT_ENTITY_UNMANAGED: 'the access it granted is no longer available',
  INVITATION_EXPIRED: 'it expired before you signed in',
  INVITATION_IDENTITY_MISMATCH: 'it was sent to a different sign-in account',
  INVITATION_NOT_ACCEPTABLE: 'it is no longer valid',
}

function reasonFor(code: string): string {
  return FAILURE_REASONS[code] ?? 'it could not be completed'
}

function inviterLabel(failure: PendingAccessFailure): string {
  return failure.inviterName || failure.inviterEmail || 'the person who invited you'
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/5 grid max-w-prose gap-2 rounded-md border p-4"
    >
      <p className="text-foreground text-sm font-medium">{title}</p>
      {children}
    </div>
  )
}

export function PendingAccessNotice() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const impersonating = user?.impersonation != null
  const scopedEntityId = user?.entityId
  const { mutate, data, isError } = useAcceptPendingInvitations()
  const claimed = useRef(false)

  useEffect(() => {
    if (impersonating || claimed.current) return
    claimed.current = true
    mutate(undefined, {
      onSuccess: (result) => {
        if (!result.granted) return
        void hydrateIdentity(scopedEntityId).then(() => queryClient.invalidateQueries())
      },
    })
  }, [impersonating, mutate, scopedEntityId, queryClient])

  if (impersonating) return null

  if (isError)
    return (
      <Notice title="We couldn’t check for pending invitations">
        <p className="text-muted-foreground text-sm">
          If you were invited and can’t see what you expect, reload the page. If it keeps
          happening, ask your administrator to send a new invitation.
        </p>
      </Notice>
    )

  if (!data || data.failures.length === 0) return null

  return (
    <Notice
      title={
        data.granted
          ? 'Some of your access couldn’t be applied'
          : 'An invitation couldn’t be applied'
      }
    >
      {data.failures.map((failure, index) => (
        <p key={`${failure.code}-${index}`} className="text-muted-foreground text-sm">
          Your invitation from {inviterLabel(failure)} couldn’t be applied because{' '}
          {reasonFor(failure.code)}.{' '}
          {failure.inviterEmail ? (
            <>
              Contact{' '}
              <a className="text-primary underline" href={`mailto:${failure.inviterEmail}`}>
                {failure.inviterEmail}
              </a>{' '}
              for a new invitation.
            </>
          ) : (
            <>Ask your administrator for a new invitation.</>
          )}
        </p>
      ))}
    </Notice>
  )
}
